import type { SearchResultItem } from 'owa-timestream-search-orchestration';
import type { AssetId, TimeStreamAsset } from 'owa-timestream-types';
import { getCapturedAssets, getSelectedProjectId } from 'owa-timestream-store';
import type Item from 'owa-service/lib/contract/Item';
import { userDate, compare, OwaDate } from 'owa-datetime';
import { getTimeGroupHeader, TimeGroupHeader } from 'owa-mail-group-headers';
import type { IMapEntries } from 'mobx';

export default function capturedItems(): SearchResultItem[] {
    const projectId = getSelectedProjectId();
    const sortedAssets = getCapturedAssets(projectId).sort(([id1, asset1], [id2, asset2]) => {
        return compare(
            userDate(asset2.lastModifiedDateTime),
            userDate(asset1.lastModifiedDateTime)
        );
    });
    const headers = getHeaders(sortedAssets);
    let headerIndex = 0;

    const searchResultsAndHeaders: SearchResultItem[] = [];
    sortedAssets.forEach(([id, asset]) => {
        while (
            headerIndex < headers.length &&
            compare(userDate(asset.lastModifiedDateTime), headers[headerIndex].time) < 0
        ) {
            searchResultsAndHeaders.push(headers[headerIndex].headerItem);
            headerIndex++;
        }
        const item = (asset.payload as { item: Item })?.item;
        if (!item) {
            return;
        }
        const result = {
            type: asset.type,
            id: item.ItemId.Id,
            data: item,
            matchingTerms: [],
            existingAssetId: id,
        } as SearchResultItem;

        searchResultsAndHeaders.push(result);
    });

    return searchResultsAndHeaders;
}

/**
 * Callback function to return the item headers to inject into the search results
 */
function getHeaders(
    sortedAssets: IMapEntries<AssetId, TimeStreamAsset>
): { headerItem: SearchResultItem; time: OwaDate }[] {
    const headerMap: { [key: string]: TimeGroupHeader } = {};
    sortedAssets.forEach(([id, asset]) => {
        const date = userDate(asset.lastModifiedDateTime);
        const header = getTimeGroupHeader(date);
        headerMap[header.headerId] = header;
    });

    const headersToReturn: { headerItem: SearchResultItem; time: OwaDate }[] = [];
    let previousHeaderStartTime: OwaDate | null = null;
    Object.keys(headerMap).forEach(key => {
        const headerData = headerMap[key];
        const time = previousHeaderStartTime || headerData.rangeEndTime;
        const searchResultItem = {
            type: 'mailItem', // this ensures that we use the same header as the mail items, since the captured item headers are identical
            id: 'mailItem_header_' + time,
            data: {
                DateTimeReceived: time,
                headerText: headerData.headerText(),
            },
            matchingTerms: [],
        } as SearchResultItem;

        previousHeaderStartTime = headerData.rangeStartTime;
        const header = {
            headerItem: searchResultItem,
            time: time,
        };
        headersToReturn.push(header);
    });

    return headersToReturn.sort((header1, header2) => compare(header2.time, header1.time));
}
